/**
 * Public "About us" page — served at `/about`.
 *
 * Content is fetched from the shared `landing_content` singleton via
 * `GET /landing/content` (no auth required). On any failure or for older
 * rows missing the `aboutPage` blob, we fall back to
 * `DEFAULT_LANDING_CONTENT.aboutPage` so the page always renders.
 */
import {
  DEFAULT_LANDING_CONTENT,
  type AboutCredential,
  type AboutService,
  type LandingContentView,
} from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';
import {
  ArrowRight,
  Award,
  Building2,
  CheckCircle2,
  Globe,
  Layers,
  LogIn,
  Network,
  Plane,
  Smartphone,
  Sparkles,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';


const DEFAULT_ABOUT = DEFAULT_LANDING_CONTENT.aboutPage!;

const FALLBACK_CREDENTIAL_ICONS: LucideIcon[] = [Award, CheckCircle2, Globe];
const FALLBACK_SERVICE_ICONS: LucideIcon[] = [
  Layers,
  Network,
  Building2,
  Plane,
  Smartphone,
  Sparkles,
];

function lookupIcon(name: string, fallback: LucideIcon): LucideIcon {
  const found = (LucideIcons as unknown as Record<string, LucideIcon | undefined>)[name];
  return found ?? fallback;
}

export function AboutPage(): JSX.Element {
  const { user } = useAuth();
  const contentQuery = useQuery({
    queryKey: ['landing', 'content'],
    queryFn: () => apiRequest<LandingContentView>('/landing/content'),
    staleTime: 60_000,
  });
  const ap = contentQuery.data?.content.aboutPage ?? DEFAULT_ABOUT;

  return (
    <div className="min-h-screen bg-paper text-ink antialiased">
      <PublicNav user={user} />

      <main className="mx-auto max-w-5xl px-6 py-16 sm:py-24">
        <header className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
            {ap.eyebrow}
          </p>
          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
            {ap.title}
          </h1>
          {ap.subtitle && (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-ink-muted">
              {ap.subtitle}
            </p>
          )}
        </header>

        {/* Story */}
        <section className="mt-16 grid gap-12 md:grid-cols-[1fr_2fr]">
          <h2 className="text-2xl font-semibold tracking-tight text-ink">{ap.storyTitle}</h2>
          <div className="space-y-4 text-sm leading-relaxed text-ink-muted">
            {ap.storyBody.split(/\n\n+/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </section>

        {/* Credentials */}
        {ap.credentials.length > 0 && (
          <section className="mt-20 grid gap-4 sm:grid-cols-3">
            {ap.credentials.map((c, idx) => (
              <CredentialCard
                key={idx}
                credential={c}
                fallback={FALLBACK_CREDENTIAL_ICONS[idx % FALLBACK_CREDENTIAL_ICONS.length]!}
              />
            ))}
          </section>
        )}

        {/* Services */}
        {ap.services.length > 0 && (
          <section className="mt-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              {ap.servicesEyebrow}
            </p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
              {ap.servicesTitle}
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {ap.services.map((s, idx) => (
                <ServiceCard
                  key={idx}
                  service={s}
                  fallback={FALLBACK_SERVICE_ICONS[idx % FALLBACK_SERVICE_ICONS.length]!}
                />
              ))}
            </div>
          </section>
        )}

        {/* Why work with us */}
        {ap.whyPoints.length > 0 && (
          <section className="mt-20 rounded border border-rule bg-paper p-8 sm:p-10">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                  {ap.whyEyebrow}
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
                  {ap.whyTitle}
                </h2>
                {ap.whyBody && (
                  <p className="mt-4 text-sm leading-relaxed text-ink-muted">{ap.whyBody}</p>
                )}
              </div>
              <ul className="space-y-3 text-sm text-ink-muted">
                {ap.whyPoints.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-positive" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Notable clients */}
        <section className="mt-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
            {ap.clientsEyebrow}
          </p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
            {ap.clientsTitle}
          </h2>
          {ap.clientsBody && (
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-ink-muted">{ap.clientsBody}</p>
          )}
        </section>

        {/* CTA */}
        <section className="mt-20 rounded border border-rule bg-paper p-8 text-center sm:p-12">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-4">
            <Users className="size-8 text-accent" />
            <h2 className="text-2xl font-semibold tracking-tight text-ink">{ap.ctaTitle}</h2>
            {ap.ctaBody && <p className="text-sm text-ink-muted">{ap.ctaBody}</p>}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
              >
                Get in touch
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-md border border-rule bg-paper-soft px-5 py-2.5 text-sm font-medium text-ink transition hover:border-accent"
              >
                Back to AI Portal
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small per-page components — kept local to avoid touching shared shell code.
// The NavBar / Footer here intentionally mirror the public landing page so
// the three public surfaces feel like one cohesive site.
// ---------------------------------------------------------------------------

function CredentialCard({
  credential,
  fallback,
}: {
  credential: AboutCredential;
  fallback: LucideIcon;
}): JSX.Element {
  const Icon = lookupIcon(credential.icon, fallback);
  return (
    <div className="rounded border border-rule bg-paper p-5">
      {/* eslint-disable-next-line react-hooks/static-components -- lookupIcon returns an existing lucide component from a map, it does not create one */}
      <Icon className="size-5 text-accent" />
      <p className="mt-3 text-2xl font-semibold tracking-tight text-ink">{credential.value}</p>
      <p className="mt-1 text-xs leading-relaxed text-ink-muted">{credential.label}</p>
    </div>
  );
}

function ServiceCard({
  service,
  fallback,
}: {
  service: AboutService;
  fallback: LucideIcon;
}): JSX.Element {
  const Icon = lookupIcon(service.icon, fallback);
  return (
    <div className="rounded border border-rule bg-paper p-5 transition hover:border-accent/40 hover:shadow-sm">
      <div className="grid size-10 place-items-center rounded-tight bg-accent/10 text-accent">
        {/* eslint-disable-next-line react-hooks/static-components -- lookupIcon returns an existing lucide component from a map, it does not create one */}
        <Icon className="size-5" />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-ink">{service.title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-ink-muted">{service.description}</p>
    </div>
  );
}

function PublicNav({ user }: { user: { id: string; role: string } | null }): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const dashboardHref = user?.role === 'admin' ? '/admin/users' : '/chat';

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-300',
        scrolled
          ? 'border-b border-rule/60 bg-paper/85 backdrop-blur-md shadow-sm supports-[backdrop-filter]:bg-paper/65'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-6">
        <Link to="/" className="flex items-center gap-2.5 transition hover:opacity-90">
          <img
            src="/peerless-logo.png"
            alt="Peerless"
            className="h-7 w-auto select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
            draggable={false}
          />
          <span className="hidden text-sm font-semibold tracking-tight text-ink sm:inline">
            AI Portal
          </span>
        </Link>
        <nav className="hidden items-center gap-6 text-sm text-ink-muted md:flex">
          <Link to="/about" className="text-ink">
            About
          </Link>
          <Link to="/contact" className="transition hover:text-ink">
            Contact
          </Link>
          <a
            href="/wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            Wiki
          </a>
        </nav>
        <Link
          to={user ? dashboardHref : '/login'}
          className="inline-flex items-center gap-1.5 rounded-tight bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
        >
          {user ? <ArrowRight className="size-3.5" /> : <LogIn className="size-3.5" />}
          {user ? 'Open dashboard' : 'Sign in'}
        </Link>
      </div>
    </header>
  );
}

function PublicFooter(): JSX.Element {
  return (
    <footer className="border-t border-rule/60 bg-paper">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10 text-xs text-ink-muted">
        <nav
          aria-label="Footer"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:justify-start"
        >
          <Link to="/about" className="transition hover:text-ink">
            About us
          </Link>
          <Link to="/contact" className="transition hover:text-ink">
            Contact us
          </Link>
          <a
            href="/wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            Wiki
          </a>
          <Link to="/login" className="transition hover:text-ink">
            Sign in
          </Link>
        </nav>
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img
              src="/peerless-logo.png"
              alt="Peerless"
              className="h-5 w-auto opacity-80 select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
              draggable={false}
            />
            <span className="text-ink/80">AI Portal</span>
          </div>
          <p>© Peerless Technology — built for teams that ship.</p>
        </div>
      </div>
    </footer>
  );
}
